<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { date as formatDate } from '@bignerve/ui-utils';
import { useAuth } from '/app/composables/auth';
import Markdown from '/app/components/NMarkdown.vue';
import NIcon from '/app/components/NIcon.vue';
import { renderHighlight } from './template-context';

const props = defineProps({
    detail: {
        type: Object,
    },
    simple: {
        type: Boolean,
        default: false,
    },
});

const { auth: $auth } = useAuth();
const router = useRouter();
const { t } = useI18n();

// Role glyph + colour, mirroring the admin highlight editor's role options
// (bignerve-admin HighlightEdition.vue); change both sides together.
const ROLES = {
    catalyst: { icon: 'catalyst', color: 'text-catalyst' },
    innovator: { icon: 'innovator', color: 'text-innovator' },
    mentor: { icon: 'mentor', color: 'text-mentor' },
    analyst: { icon: 'analyst', color: 'text-rater' },
    rater: { icon: 'rater', color: 'text-rater' },
    director: { icon: 'director', color: 'text-director' },
    scout: { icon: 'scout', color: 'text-scout' },
    patron: { icon: 'patron', color: 'text-patron' },
    commentator: { icon: 'discussion', color: 'text-commentator' },
    social: { icon: 'lightning', color: 'text-black' },
    activity: { icon: 'lightning', color: 'text-nerve' },
    money: { icon: 'money-bill-alt-regular', color: 'text-malachite' },
    you: { icon: 'user-avatar-regular', color: 'text-green-600' },
};

// Glyphs the payload's `icon` may name once its `-icon` suffix is dropped
// ('innovator-icon' -> icon-innovator). Anything outside the set falls back to
// the legacy role.event map, then to the role's own glyph, so an unknown name
// never renders a blank glyph.
const PAYLOAD_ICONS = [
    ...new Set([...Object.values(ROLES).map((role) => role.icon), 'question', 'user']),
];

const icon = computed(() => {
    const named = String(props.detail.icon || '').replace(/-icon$/, '');
    const role = ROLES[props.detail.role];

    // Current payloads carry the glyph; the role only picks its colour.
    if (PAYLOAD_ICONS.includes(named)) {
        return [named, role?.color, 'text-lg'].filter(Boolean).join(' ');
    }

    // Legacy payloads carry no icon — derive one from role + event.
    const icons = {
        'Catalyst.ChallengePublished': 'question-circle-solid text-catalyst',
        'Catalyst.ChallengeClosed': 'question-circle-solid text-catalyst',
        'Innovator.IdeaMentored': 'lightbulb-o1 text-innovator',
        'Analyst.UserBrainScoreUpdated': 'arrow-solid-circle-up text-analyst',
        'Innovator.UserBrainScoreUpdated': 'arrow-solid-circle-up text-innovator',
    };

    const matcher = `${props.detail.role}.${props.detail.event}`;
    const key = Object.keys(icons).find(
        key => matcher.match(new RegExp(key, 'i'))
    );

    if (icons[key]) return `${icons[key]} text-lg`;

    return role ? `${role.icon} ${role.color} text-lg` : 'lightning text-lg';
});

// Highlights can arrive with Go's zero timestamp (0001-01-01T00:00:00Z) when the
// event carries no date of its own; rendering that as "2025 years ago" is worse
// than showing nothing, so the meta line is dropped instead.
const createdAt = computed(() => {
    const stamp = Date.parse(props.detail.created_at);

    if (Number.isNaN(stamp) || stamp <= 0) return null;

    return formatDate(props.detail.created_at, 'human');
});

const meta = computed(() => [
    createdAt.value,
    props.detail.repeats > 1 && t('component.game-dash.repeats', props.detail.repeats),
].filter(Boolean).join(' · '));

// Markdown produced by the highlight's admin template (an object still needing
// the Handlebars pass over its template.* fields, or a string the backend
// already rendered), with its single-brace tokens — e.g. "A Catalyst you
// follow, {catalyst.name}, created a challenge
// [{challenge.title}]({route.challenges.show})." — substituted from the
// `contexts` entities hydrated out of `template_vars` and the route link
// tokens. useHighlights renders it ahead to collapse repeats; items reaching
// here another way are rendered on the spot. Null when the item carries no
// renderable template: nothing is rendered.
const statement = computed(() => props.detail.statement
    ?? renderHighlight(props.detail, { router, user: $auth.user }));
</script>

<template>
    <div v-if="statement" class="game-dash-list-item" data-heap-id="gamedash-notification" data-heap-notification-type="highlight" :class="`is-${detail.role}`">
        <div class="game-dash-list-item__point game-dash-list-item__point--icon">
            <NIcon :as="icon" />
        </div>
        <div class="game-dash-list-item__content">
            <Markdown
                :code="statement"
                class="game-dash-list-item__text opacity-95 leading-5"
                inline
            />
            <div v-if="!simple && meta" class="game-dash-list-item__meta">{{ meta }}</div>
        </div>
    </div>
</template>
